import React, { useEffect, useState } from 'react';
import HeaderCard from '../../../components/Header/HeaderCard';
import TableList from '../../../components/Tools/TableList';
import { apiService } from '../../../components/Config/api.service';
import { FiDownload } from 'react-icons/fi';
import { FaMoneyBillWave, FaMoneyCheckAlt, FaUserTag, FaBuilding } from 'react-icons/fa';
import PayslipHeader from './PayslipHeader';

function Payslips({ employeeId, setActiveTab }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterBy, setFilterBy] = useState({}); 
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
    const [totals, setTotals] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 15,
        total_pages: 1,
        total_count: 0
    });

    const downloadPayslipPDF = (payslipId) => {
        apiService.getBlobFile(`/payslip/pdf/${payslipId}`)
            .then(response => {
                if (response && response.data) {
                    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `bulletin_${payslipId}.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                }
            })
            .catch(error => {
                console.error('Erreur lors du téléchargement du PDF:', error);
                alert("Erreur lors du téléchargement du PDF");
            });
    };

    const columns = [
        {
            key: 'Download',
            label: ' ',
            render: (item) => (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        downloadPayslipPDF(item.id);
                    }}
                    className="text-[--color-primary] hover:text-[--color2]"
                >
                    <FiDownload className="text-lg" aria-label="download" />
                </button>
            )
        },
        { key: 'Employee', label: 'Employé' },
        {
            key: 'State',
            label: 'État',
            render: (item) => {
                let badgeColor;
                switch (item.State.toLowerCase()) {
                    case 'draft':
                        badgeColor = 'bg-yellow-100 text-yellow-800';
                        break;
                    case 'verify':
                        badgeColor = 'bg-yellow-100 text-green-800';
                        break;
                    case 'done':
                        badgeColor = 'bg-green-100 text-green-800';
                        break;
                    case 'refused':
                        badgeColor = 'bg-red-100 text-red-800';
                        break;
                    default:
                        badgeColor = 'bg-gray-100 text-gray-800';
                }

                return (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${badgeColor}`}>
                        {item.State === 'draft' ? 'Brouillon' :
                            item.State === 'verify' ? 'En attente' :
                                item.State === 'done' ? 'Validé' :
                                    item.State === 'refused' ? 'Refusé' : item.State}
                    </span>
                );
            }
        },
        {
            key: 'Period',
            label: 'Mois',
            render: (item) => {
                const dateFrom = new Date(item.De);
                return dateFrom.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
            }
        },
        {
            key: 'gross_salary',
            label: 'Salaire Brut',
            render: (item) => `${Number(item.gross_salary).toLocaleString('fr-FR')} CFA`
        },
        {
            key: 'net_salary',
            label: 'Salaire Net',
            render: (item) => `${Number(item.net_salary).toLocaleString('fr-FR')} CFA`
        },
        {
            key: 'Cotisations Salariales',
            label: 'C Salarial',
            render: (item) => `${Number(item.cotisations_salariales).toLocaleString('fr-FR')} CFA`
        },
        {
            key: 'Cotisations Patronales',
            label: 'C Patronal',
            render: (item) => `${Number(item.cotisations_patronales).toLocaleString('fr-FR')} CFA`
        }
    ];

    const fetchPayslips = (page = 1, search = '', filter = '', month = '') => {
        setLoading(true);

        // Calculate offset based on page number
        const offset = (page - 1) * pagination.limit;

        // Construct query params
        const params = new URLSearchParams();
        params.append('offset', offset);
        params.append('limit', pagination.limit);
        if (search) {
            params.append('search', search);
        }
        if (filter && Object.keys(filter).length > 0) {
            params.append('filter_by', JSON.stringify(filter));
        }
        if (month) {
            params.append('month', month);
        }

        let endpoint = `/payslip?${params.toString()}`;

        apiService.getElement(endpoint)
            .then(response => {
                const payslipsWithPeriod = response.data.payslips.map(payslip => ({
                    ...payslip,
                    Period: payslip.De // La période sera formatée dans le render de la colonne
                }));
                setData(payslipsWithPeriod);
                setTotals(response.data.totals || null);

                const responseData = response.data;
                if (responseData.pagination) {
                    const currentPage = Math.floor(responseData.pagination.offset / responseData.pagination.limit) + 1;

                    setPagination({
                        ...responseData.pagination,
                        page: currentPage
                    });
                }
                setLoading(false);
            })
            .catch(error => {
                console.error('There was an error fetching the data!', error);
                setData([]);
                setPagination({
                    page: 1,
                    limit: 5,
                    total_pages: 1,
                    total_count: 0
                });
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchPayslips(pagination.page, searchTerm, filterBy, selectedMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeId, pagination.page, searchTerm, filterBy, selectedMonth]);

    const handlePageChange = (page, search, filter) => {
        const newSearchTerm = search !== undefined ? search : searchTerm;
        const newFilterBy = filter !== undefined ? filter : filterBy;

        if (newSearchTerm !== searchTerm) {
            setSearchTerm(newSearchTerm);
        }
        if (newFilterBy !== filterBy) {
            setFilterBy(newFilterBy);
        }

        fetchPayslips(page, newSearchTerm, newFilterBy, selectedMonth);
    };

    const goBack = () => {
        if (setActiveTab) {
            setActiveTab('Bulletin');
        }
        else {
            window.history.back();
        }
    };

    const handleMonthChange = (event) => {
        setSelectedMonth(event.target.value);
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page on month change
    };

    const formatCurrency = (value) => {
        return `${Number(value || 0).toLocaleString('fr-FR')} CFA`;
    };

    const getStatsIcon = (type) => {
        const iconProps = { className: "text-white", size: 24 };
        switch (type) {
            case 'gross': return <FaMoneyBillWave {...iconProps} />;
            case 'net': return <FaMoneyCheckAlt {...iconProps} />;
            case 'salarial': return <FaUserTag {...iconProps} />;
            case 'patronal': return <FaBuilding {...iconProps} />;
            default: return null;
        }
    };

    return (
        <div>
            <div className="">
                <div className="flex items-center justify-between">
                    <HeaderCard
                        title="Historique des Bulletins"
                        text="Gérez les Bulletins de votre entreprise en toute simplicité."
                    />
                    <PayslipHeader 
                        selectedMonth={selectedMonth}
                        handleMonthChange={handleMonthChange}
                        isHistoryView={true}
                        onBackFromHistory={goBack}
                    />
                </div>

                {totals && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 my-4">
                        {[
                            { title: "Total Brut", value: formatCurrency(totals.gross_salary), type: 'gross', subtitle: "Masse salariale brute du mois" },
                            { title: "Total Net à Payer", value: formatCurrency(totals.net_salary), type: 'net', subtitle: "Net à payer aux employés" },
                            { title: "Total C. Salariales", value: formatCurrency(totals.cotisations_salariales), type: 'salarial', subtitle: "Part des employés" },
                            { title: "Total C. Patronales", value: formatCurrency(totals.cotisations_patronales), type: 'patronal', subtitle: "Part de l'employeur" }
                        ].map(({ title, value, type, subtitle }) => (
                            <div key={title} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-300">
                                <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                        <p className="text-sm font-medium uppercase tracking-wide text-gray-600">
                                            {title}
                                        </p>
                                        <p className="text-xl font-bold mt-2 text-gray-800">{value}</p>
                                        <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
                                    </div>
                                    <div className="bg-[--color-primary] rounded-full p-3 ml-4">
                                        {getStatsIcon(type)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="">
                    {loading ? (
                        <div className="flex justify-center p-8">
                            <div className="loading loading-spinner text-primary"></div>
                        </div>
                    ) : (
                        <TableList
                            searchQuery={searchTerm}
                            setSearchQuery={setSearchTerm}
                            filterBy={filterBy}
                            setFilterBy={setFilterBy}
                            columns={columns}
                            data={data}
                            pagination={pagination}
                            onPageChange={handlePageChange}
                            model={'payslip'}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

export default Payslips;